import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Strike from "@tiptap/extension-strike";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import BaseImage from "@tiptap/extension-image";
import { Mark, Node, mergeAttributes } from "@tiptap/core";
import axios from "../services/axiosInstance";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { asBlob } from "html-docx-js-typescript";
import QRCode from 'qrcode';

// Ícones Lucide
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3, Pilcrow,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo2, Redo2, Maximize2, Minimize2, 
  FileUp, Table as TableIcon, Image as ImageIcon, Eraser, Code2, MoreVertical, 
  CaseUpper, CaseLower, ChevronDown, BookOpen, Printer, FileDown, PenTool, 
  Strikethrough, UserCheck, Users, Trash2, FileText, Stamp, Grid, Eye, EyeOff, 
  Plus, Minus, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, SquareX, ClipboardPaste,
  LayoutTemplate
} from "lucide-react";

import styles from "./Modals/Modal.module.css";

/* =======================
   CSS INTERNO
======================= */
const internalCSS = `
/* TIPTAP BASE */
.tiptap { outline: none !important;
    width: 100%;
    max-width: 100%;
    background-color: rgb(255, 255, 255);
    padding: 10px;
    box-shadow: none;
    outline: none;
    overflow: auto; }
.tiptap p { margin-bottom: 0.5rem; line-height: 1.5; color: inherit; } 

/* FORMATAÇÃO BÁSICA */
.tiptap strong, .tiptap b { font-weight: 700 !important; color: inherit; }
.tiptap em, .tiptap i { font-style: italic !important; color: inherit; }
.tiptap u { text-decoration: underline !important; color: inherit; }
.tiptap s, .tiptap strike { text-decoration: line-through !important; color: inherit; }
.tiptap h1, .tiptap h2, .tiptap h3 { font-weight: 700; color: #111; }

/* LEGISLATIVO */
.tiptap p.artigo { font-weight: 600; margin: .5rem 0; }
.tiptap p.inciso { margin-left: 1.5rem; }
.tiptap p.alinea { margin-left: 2.5rem; }
.tiptap p.item  { margin-left: 3rem; }

/* Quebra de página */
hr.page-break { border: none; border-top: 1px dashed #bbb; margin: 16px 0; }
@media print {
 hr.page-break { page-break-after: always; border: none; margin: inherit; }
 .block-kebab, .tiptap-host .tiptap-editing .law-block, .bubble-menu { display: none !important; }
 .tiptap { border: none !important; box-shadow: none !important; padding: 0 !important; margin: 0 !important; }
}

/* IMAGENS */
.tiptap img { display: block; max-width: 100%; height: auto; }
.tiptap img.ProseMirror-selectednode { outline: 2px solid #0056b3; }

/* --- TABELAS: LÓGICA DE BORDA CORRIGIDA --- */

/* 1. Estilo Padrão (Com Borda) */
.tiptap table { 
    border-collapse: collapse; 
    width: 100%; 
    margin: 10px 0; 
    table-layout: fixed; 
}
.tiptap table th, 
.tiptap table td { 
    border: 1px solid #000; /* Borda preta padrão */
    padding: 6px; 
    vertical-align: top; 
    position: relative; 
}
.tiptap table th { background: #f6f6f6; font-weight: 600; }

/* 2. Estilo "Sem Borda" (Modo Edição) 
   Usamos !important para garantir que sobrescreva o padrão */
.tiptap table.no-border, 
.tiptap table.no-border th, 
.tiptap table.no-border td {
    border: 1px dashed #ccc !important; /* Visual aid para edição */
}

/* 3. Estilo "Sem Borda" (Modo Impressão/PDF) 
   Aqui removemos totalmente */
@media print {
    .tiptap table.no-border, 
    .tiptap table.no-border th, 
    .tiptap table.no-border td {
        border: none !important;
        outline: none !important;
    }
}

/* --- BUBBLE MENU --- */
.bubble-menu { 
    display: flex; 
    flex-direction: column; 
    background-color: #fff; 
    padding: 6px; 
    border-radius: 6px; 
    border: 1px solid #ccc; 
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); 
    gap: 6px; 
    min-width: 280px;
    z-index: 50; 
}
.bubble-row { display: flex; align-items: center; gap: 8px; width: 100%; }
.bubble-header { font-weight: 800; color: #888; text-transform: uppercase; margin-bottom: 2px; margin-left: 2px; }
.bubble-menu button { 
    border: none; background: transparent; color: #555; 
    padding: 4px 6px; cursor: pointer; 
    border-radius: 4px; display: flex; align-items: center; justify-content: center;
    transition: background-color 0.1s; height: 28px; 
}
.bubble-menu button:hover { background-color: #f0f0f0; color: #000; }
.bubble-menu button.is-active { background-color: #e6f0ff; color: #0056b3; }
.bubble-menu button.danger { color: #d32f2f; }
.bubble-menu button.danger:hover { background-color: #fee2e2; }
.btn-group { display: flex; background: #f5f5f5; border-radius: 4px; padding: 2px; gap: 1px; }
.btn-group button { height: 24px; padding: 0 6px; }
.btn-group button:hover { background: #e0e0e0; }
`;

/* =======================
   EXTENSÕES TIPTAP
======================= */
const FontSize = Mark.create({ name: "fontSize", addAttributes() { return { 'data-font-size': { default: null } } }, parseHTML: () => [{ style: 'font-size' }], renderHTML({ HTMLAttributes }) { return ['span', mergeAttributes(HTMLAttributes, { style: HTMLAttributes['data-font-size'] ? `font-size:${HTMLAttributes['data-font-size']}` : undefined }), 0] } });
const FontFamily = Mark.create({ name: "fontFamily", addAttributes() { return { 'data-font-family': { default: null } } }, parseHTML: () => [{ style: 'font-family' }], renderHTML({ HTMLAttributes }) { return ['span', mergeAttributes(HTMLAttributes, { style: HTMLAttributes['data-font-family'] ? `font-family:${HTMLAttributes['data-font-family']}` : undefined }), 0] } });
const TextTransform = Mark.create({ name: "textTransform", addAttributes() { return { transform: { default: null, parseHTML: el => el.style.textTransform, renderHTML: attrs => attrs.transform ? { style: `text-transform: ${attrs.transform}` } : {} } } }, addCommands() { return { toggleUppercase: () => ({ chain, editor }) => editor.isActive(this.name, {transform: 'uppercase'}) ? chain().unsetMark(this.name).run() : chain().setMark(this.name, {transform: 'uppercase'}).run(), toggleLowercase: () => ({ chain, editor }) => editor.isActive(this.name, {transform: 'lowercase'}) ? chain().unsetMark(this.name).run() : chain().setMark(this.name, {transform: 'lowercase'}).run() } } });
const LineHeight = Node.create({ name: "lineHeight", addGlobalAttributes() { return [{ types: ["paragraph", "heading"], attributes: { lineHeight: { default: null, parseHTML: el => el.style.lineHeight, renderHTML: attrs => attrs.lineHeight ? { style: `line-height:${attrs.lineHeight}` } : {} }, class: { default: null, parseHTML: el => el.getAttribute("class"), renderHTML: attrs => attrs.class ? { class: attrs.class } : {} } } }] } });
const PageBreak = Node.create({ name: "pageBreak", group: "block", atom: true, selectable: true, parseHTML: () => [{ tag: 'hr[data-page-break="true"]' }], renderHTML: () => ['hr', { 'data-page-break': 'true', class: 'page-break' }], addCommands() { return { insertPageBreak: () => ({ chain }) => chain().insertContent({ type: this.name }).run() } } });

const ResizableImage = BaseImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: '100%', parseHTML: el => el.style.width, renderHTML: attrs => ({ style: `width: ${attrs.width}; height: auto;` }) },
      align: { default: 'center', parseHTML: el => el.getAttribute('data-align'), renderHTML: attrs => { const style = `width: ${attrs.width}; height: auto;`; if (attrs.align === 'left') return { 'data-align': 'left', style: `${style} float: left; margin: 0.5rem 1rem 0.5rem 0;` }; if (attrs.align === 'right') return { 'data-align': 'right', style: `${style} float: right; margin: 0.5rem 0 0.5rem 1rem;` }; return { 'data-align': 'center', style: `${style} display: block; margin: 0.5rem auto;` }; } },
    };
  }
});

const CustomTable = Table.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attrs => {
            if (attrs.class) { return { class: attrs.class }; }
            return {};
        }
      }
    }
  }
});

/* =======================
   TOOLBAR COMPONENTS
======================= */
const ToolbarButton = ({ onClick, isActive, disabled, title, children, hasArrow }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} className={`${styles.ttBtn} ${isActive ? styles.ttActive : ""}`} disabled={disabled} title={title} style={{ display: "flex", alignItems: "center", gap: 3 }}>
    {children} {hasArrow && <ChevronDown size={10} style={{ opacity: 0.6 }} />}
  </button>
);

const ToolbarDropdown = ({ icon, label, title, children, isActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={ref}>
      <ToolbarButton onClick={() => setIsOpen(!isOpen)} title={title} isActive={isActive || isOpen} hasArrow>{icon} {label && <span style={{fontWeight: 500}}>{label}</span>}</ToolbarButton>
      {isOpen && <div style={{ position: 'absolute', top: '100%', left: 0, backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, border: '1px solid #ddd', borderRadius: '4px', padding: '4px', display: 'flex', flexDirection: 'column', maxHeight: '350px', overflowY: 'auto' }} onClick={() => setIsOpen(false)}>{children}</div>}
    </div>
  );
};
const DropdownItem = ({ onClick, children, title }) => <button type="button" onClick={onClick} title={title} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 10px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: "#333" }} onMouseEnter={(e)=>e.currentTarget.style.background='#f3f4f6'} onMouseLeave={(e)=>e.currentTarget.style.background='transparent'}>{children}</button>;

/* =======================
   COMPONENTE PRINCIPAL
======================= */
export default function TiptapEditor({ 
    value, 
    onChange, 
    placeholder = "Digite aqui...", 
    showDocxImport = true,
    materiaId = null,
    autores = [] // <--- Agora aceita um array de autores
}) {
  const [fullscreen, setFullscreen] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlBuffer, setHtmlBuffer] = useState("");
  const [blockMenu, setBlockMenu] = useState({ visible: false, x: 0, y: 0, kind: null });
  
  // Dados dinâmicos
  const [currentUser, setCurrentUser] = useState(null);
  const [listaVereadores, setListaVereadores] = useState([]); 
  const [modelosDisponiveis, setModelosDisponiveis] = useState([]);
  const [camaraInfo, setCamaraInfo] = useState(null);

  const fileInputRef = useRef(null);
  const imgFileInputRef = useRef(null);
  const editorContainerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
        try {
            const [meRes, vereadoresRes, modelosRes, camaraRes] = await Promise.all([
                axios.get("/usuarios/perfil").catch(() => ({ data: null })),
                axios.get("/usuarios/vereadores").catch(() => ({ data: [] })),
                axios.get("/documentos/modelos/listar", { params: { ativosOnly: true } }).catch(() => ({ data: [] })),
                axios.get("/camara-municipal").catch(() => ({ data: null }))
            ]);
            setCurrentUser(meRes.data);
            const sorted = (vereadoresRes.data || []).sort((a, b) => a.nome.localeCompare(b.nome));
            setListaVereadores(sorted);
            setModelosDisponiveis(modelosRes.data || []);
            setCamaraInfo(camaraRes.data);
        } catch (error) {
            console.error("Erro ao carregar dados do editor", error);
        }
    };
    fetchData();
  }, []);

  const uploadImage = async (file) => {
    const form = new FormData(); form.append("file", file);
    try { 
      const { data } = await axios.post("/documentos/upload", form, { headers: { "Content-Type": "multipart/form-data" } }); 
      return data?.url; 
    } catch { 
      toast.error("Erro no upload da imagem");
      return null; 
    }
  };

  const editor = useEditor({
    autofocus: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: true, autolink: true, HTMLAttributes: { target: "_blank", rel: "noopener" } }),
      Highlight, Subscript, Superscript, Strike, 
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle, Color,
      Placeholder.configure({ placeholder }),
      CharacterCount.configure(),
      ResizableImage.configure({ inline: false, allowBase64: true }),
      FontSize, FontFamily, TextTransform, LineHeight, PageBreak,
      CustomTable.configure({ resizable: true }), TableRow, TableHeader, TableCell,
    ],
    content: value ?? "",
    editorProps: { 
        attributes: { class: "tiptap" },
        handlePaste: (view, event) => {
            const items = event.clipboardData?.items || [];
            for (const it of items) {
              if (it.kind === "file" && /^image\//.test(it.type)) {
                const file = it.getAsFile();
                uploadImage(file).then(url => { if (url) editor.commands.setImage({ src: url }); });
                return true;
              }
            }
            return false;
          },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    onSelectionUpdate: ({ editor }) => {
        if (!editorContainerRef.current) return;
        const { from } = editor.state.selection;
        const domInfo = editor.view.domAtPos(from);
        let el = domInfo.node.nodeType === 1 ? domInfo.node : domInfo.node.parentElement;
        while (el && el.tagName !== "P") el = el.parentElement;
        if (!el) { setBlockMenu(s => ({ ...s, visible: false })); return; }
        const kind = ["artigo", "inciso", "alinea", "item"].find(k => el.classList.contains(k));
        if (!kind) { setBlockMenu(s => ({ ...s, visible: false })); return; }
        const rect = el.getBoundingClientRect();
        const containerRect = editorContainerRef.current.getBoundingClientRect();
        setBlockMenu({ visible: true, x: (rect.right - containerRect.left) - 10, y: (rect.top - containerRect.top) - 10, kind });
    },
  });

  useEffect(() => { if (editor && typeof value === "string" && value !== editor.getHTML()) editor.commands.setContent(value, false); }, [value, editor]);

  // --- FUNÇÕES ---
  const doImportDocx = async (file) => {
    try {
      const { default: mammoth } = await import("mammoth");
      const arrayBuffer = await file.arrayBuffer();
      const { value: html } = await mammoth.convertToHtml({ arrayBuffer }, { includeDefaultStyleMap: true });
      if (editor) { editor.commands.setContent(html, true); onChange?.(html); }
    } catch { alert("Erro ao importar DOCX."); }
  };

  const exportToDocx = async () => {
    if (!editor) return;
    const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>${internalCSS}</style></head><body>${editor.getHTML()}</body></html>`;
    const buffer = await asBlob(htmlContent, { orientation: "portrait", margins: { top: 720, right: 720, bottom: 720, left: 720 } });
    saveAs(buffer, "documento.docx");
  };

  // --- FUNÇÃO DE IMPRESSÃO ---
  const handlePrint = async (comTimbre = false) => {
    if (!editor) return;
    
    const content = editor.getHTML();
    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow) return alert("Permita popups.");
    
    // 1. Logo
    const logoUrl = camaraInfo?.logo 
        ? `${axios.defaults.baseURL}/uploads/logos/${camaraInfo.logo}` 
        : `${axios.defaults.baseURL}/images/logo_camara.png`;

    // 2. Normaliza lista de autores (garante que é array)
    // Se "autores" não for passado, array vazio. Se for objeto único, vira array.
    const listaAutores = Array.isArray(autores) ? autores : (autores ? [autores] : []);

    // 3. QR Code (Se houver ID da matéria)
    let qrCodeImgTag = '';
    if (materiaId) {
        try {
            const linkMateria = `${window.location.origin}/portal?view=materias&id=${materiaId}`;
            const qrCodeDataUrl = await QRCode.toDataURL(linkMateria, { width: 100, margin: 0 });
            qrCodeImgTag = `<img src="${qrCodeDataUrl}" alt="QR Code" style="width: 80px; height: 80px;" />`;
        } catch (error) { console.error("Erro QR", error); }
    }

    // 4. Data por Extenso
    const cidade = camaraInfo?.cidade || "Câmara Municipal";
    const dataExtenso = new Date().toLocaleDateString('pt-BR', { 
        day: 'numeric', month: 'long', year: 'numeric' 
    });
    const linhaData = `${cidade} - ${camaraInfo?.estado || ''}, ${dataExtenso}.`;

    // 5. Gera HTML das Assinaturas dos AUTORES apenas
    let assinaturasHtml = '';

    if (listaAutores.length > 0) {
        assinaturasHtml = listaAutores.map(autor => {
            let assinaturaImg = '';
            
            if (autor.assinatura_imagem) {
                const urlAssinatura = `${axios.defaults.baseURL}/images/usuarios/${autor.assinatura_imagem}`;
                assinaturaImg = `<img src="${urlAssinatura}" alt="Assinatura" style="height: 60px; display: block; margin: 0 auto;" />`;
            } else {
                // Linha manual se o autor não tiver assinatura digital
                assinaturaImg = `<div style="height: 50px; border-bottom: 1px solid #000; width: 200px; margin: 10px auto;"></div>`;
            }

            return `
                <div style="flex: 0 0 auto; text-align: center; margin: 10px 20px;">
                    <div style="height: 70px; display: flex; align-items: end; justify-content: center;">
                        ${assinaturaImg}
                    </div>
                    <div style="font-weight: bold; text-transform: uppercase; font-size: 10pt; margin-top: 5px;">
                        ${autor.nome || 'Vereador'}
                    </div>
                    <div style="font-size: 9pt;">
                        ${autor.cargo || 'Vereador'}
                    </div>
                </div>
            `;
        }).join('');
    } else {
        // Se não tiver autor nenhum, deixa um espaço manual genérico ou vazio
        assinaturasHtml = `
            <div style="text-align: center; margin-top: 20px;">
                 <div style="height: 50px; border-bottom: 1px solid #000; width: 250px; margin: 0 auto 10px auto;"></div>
                 <div style="font-size: 10pt;">Responsável</div>
            </div>
        `;
    }

    // 6. Bloco Final (Cidade/Data + Assinaturas + QR)
    const blocoFinal = `
        <div class="footer-final" style="margin-top: 60px; text-align: center; break-inside: avoid; page-break-inside: avoid;">
            
            <p style="margin-bottom: 40px; font-family: 'Times New Roman', serif; font-size: 11pt;">
                ${linhaData}
            </p>

            <div class="assinaturas-container" style="display: flex; flex-wrap: wrap; justify-content: center; margin-bottom: 20px;">
                ${assinaturasHtml}
            </div>

            ${qrCodeImgTag ? `
            <div class="qrcode-container" style="margin-top: 20px; display: flex; flex-direction: column; align-items: center;">
                ${qrCodeImgTag}
                <div style="font-size: 8px; font-family: sans-serif; color: #555;">Validar Autenticidade</div>
            </div>
            ` : ''}

        </div>
    `;

    // 7. Cabeçalho
    const header = comTimbre ? `
        <div class="header" style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #000; margin-bottom: 20px; padding-bottom: 10px; padding-top: 10px;">
            <div style="flex: 0 0 auto; margin-right: 15px;">
                <img src="${logoUrl}" alt="Logo" style="height: 80px; width: auto; object-fit: contain;" />
            </div>
            <div style="flex: 1; text-align: right; color: #333; font-family: 'Times New Roman', serif;">
                <h1 style="margin: 0; font-size: 16pt; text-transform: uppercase; font-weight: bold;">${camaraInfo?.nome || "Câmara Municipal"}</h1>
                <p style="margin: 4px 0 0; font-size: 10pt;">${camaraInfo?.endereco || ''}</p>
                <p style="margin: 0; font-size: 10pt;">${camaraInfo?.cidade || ''} - ${camaraInfo?.estado || ''} | CEP: ${camaraInfo?.cep || ''}</p>
                <p style="margin: 0; font-size: 10pt;">${camaraInfo?.telefone ? `Tel: ${camaraInfo.telefone}` : ''} ${camaraInfo?.email ? ` | ${camaraInfo.email}` : ''}</p>
                ${camaraInfo?.site ? `<p style="margin: 0; font-size: 9pt;">${camaraInfo.site}</p>` : ''}
            </div>
        </div>
    ` : '';

    const footerSystem = comTimbre ? `
        <div class="footer" style="position:fixed; bottom:0; left:0; right:0; text-align:center; font-size:9pt; border-top:1px solid #ddd; background:#fff;">
            Documento gerado pelo Sistema Plenário Digital
        </div>` : '';

    printWindow.document.write(`
        <html>
            <head>
                <title>Imprimir</title>
                <style>
                    @page { size: A4; margin: 2cm; }
                    body { font-family: 'Times New Roman', serif; margin: 0; } 
                    ${internalCSS}
                </style>
            </head>
            <body>
                ${header}
                <div class="tiptap">
                    ${content}
                    ${blocoFinal}
                </div>
                ${footerSystem}
                <script>
                    window.onload = function() { window.print(); }
                </script>
            </body>
        </html>
    `);
    printWindow.document.close();
  };

  // --- HELPERS LEGISLATIVOS ---
  const toRoman = (n) => { if (!n) return "I"; const map = [[1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"], [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]]; let out = "", i = 0; while (n > 0) { const [v, s] = map[i]; if (n >= v) { out += s; n -= v; } else i++; } return out; };
  const countSinceLastArtigo = (doc, selection, target) => {
    let lastArtigoIndex = -1, offset = 0;
    const selPos = selection.$from.pos;
    doc.descendants((node) => { if (node.isTextblock) { if (node.attrs?.class === "artigo") lastArtigoIndex = offset; offset++; } return true; });
    offset = 0; let count = 0;
    doc.descendants((node, pos) => { if (node.isTextblock) { if (lastArtigoIndex >= 0 && offset > lastArtigoIndex && pos <= selPos) { if (node.attrs?.class === target) count++; } offset++; } return true; });
    return count;
  };
  const inserirArtigo = () => { const n = (editor.getHTML().match(/>Art\.\s*(\d+)[ºo]\s*—?/gi) || []).length + 1; editor.chain().focus().insertContent(`<p class="artigo">Art. ${n}º — </p>`).run(); };
  const inserirInciso = () => { const c = countSinceLastArtigo(editor.state.doc, editor.state.selection, "inciso"); editor.chain().focus().insertContent(`<p class="inciso">${toRoman(c + 1)} — </p>`).run(); };
  const inserirAlinea = () => { const c = countSinceLastArtigo(editor.state.doc, editor.state.selection, "alinea"); editor.chain().focus().insertContent(`<p class="alinea">(${String.fromCharCode(97 + (c % 26))}) — </p>`).run(); };
  
  const inserirAssinaturaHtml = (htmlContent) => {
    if (editor.isActive('table')) {
        editor.chain().focus().addColumnAfter().insertContent(htmlContent).run();
    } else {
        editor.chain().focus().insertContent(`
            <table class="no-border" style="width: 100%; margin-top: 40px; border: none;">
                <tbody style="border: none;"><tr style="border: none;"><td style="text-align: center; vertical-align: bottom; padding: 10px; border: none;">
                    ${htmlContent}
                </td></tr></tbody>
            </table><p></p>
        `).run();
    }
  };
  const inserirBlocoAssinaturaManual = () => {
    const conteudo = `<div style="height:50px"></div><div style="border-top: 1px solid #000; width: 80%; margin: 10px auto; min-width: 150px;"></div>`;
    inserirAssinaturaHtml(conteudo);
  };
  const inserirAssinaturaUsuario = (usuario) => {
    if (!usuario) return toast.error("Usuário inválido.");
    const urlAssinatura = usuario.assinatura_imagem ? `${axios.defaults.baseURL}/images/usuarios/${usuario.assinatura_imagem}` : null;
    const imgTag = urlAssinatura ? `<img src="${urlAssinatura}" alt="Assinatura" style="height: 70px; display: block; margin: 0 auto;" />` : `<div style="height: 60px; color: #bbb;"></div>`;
    inserirAssinaturaHtml(imgTag);
  };

  // --- MODELOS ---
  const aplicarModelo = (htmlModelo) => {
    if(!editor) return;
    if(!editor.isEmpty && !window.confirm("Isso substituirá o conteúdo atual. Continuar?")) return;
    editor.commands.setContent(htmlModelo);
    toast.success("Modelo aplicado!");
  };

  // --- TOGGLE BORDER ---
  const toggleTableBorder = () => {
    if (!editor) return;
    const currentClass = editor.getAttributes('table').class;
    const hasNoBorder = currentClass && currentClass.includes('no-border');
    editor.chain().focus().updateAttributes('table', { 
        class: hasNoBorder ? null : 'no-border' 
    }).run();
  };
  
  const insertTable = () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  const isTableBorderHidden = editor && editor.getAttributes('table').class?.includes('no-border');

  const BubbleBtn = ({ onClick, children, className, title, style }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} className={className} title={title} style={style}>
        {children}
    </button>
  );

  const handlePasteHtml = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) { editor.chain().focus().insertContent(text).run(); toast.success("HTML colado!"); }
    } catch { toast.error("Erro ao ler clipboard."); }
  };

  if (!editor) return null;

  return (
    <div 
        className={fullscreen ? styles.modal : ""}
        style={{ borderRadius: fullscreen ? 0 : '3px', background: '#f9f9f9', display:'flex', flexDirection:'column', width: fullscreen ? "100vw" : "100%", height: fullscreen ? "100vh" : "auto", boxSizing: "border-box", position: fullscreen ? 'fixed' : 'relative', top: fullscreen ? 0 : 'auto', left: fullscreen ? 0 : 'auto', zIndex: fullscreen ? 9999 : 'auto', padding: fullscreen ? 0 : 0 }}
    >
       <style>{internalCSS}</style>
       
       {fullscreen && (
         <button onClick={() => setFullscreen(false)} className={styles.modalClose}>
           <SquareX />
         </button>
       )}

       {/* --- TOOLBAR --- */}
       <div className={styles.toolbar} onMouseDown={(e) => e.preventDefault()}>
          <div style={{display:'flex', gap:2}}>
             <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo2 size={15} /></ToolbarButton>
             <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo2 size={15} /></ToolbarButton>
          </div>
          <div style={{width:1, background:'#ccc', margin:'0 4px'}} />

          <ToolbarDropdown icon={<Bold size={15} />} title="Formatação" isActive={editor.isActive('bold')}>
             <DropdownItem onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14}/> Negrito</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14}/> Itálico</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={14}/> Sublinhado</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={14}/> Riscar</DropdownItem>
             <div style={{height:1, background:'#eee', margin:'2px 0'}}/>
             <DropdownItem onClick={() => editor.chain().focus().toggleUppercase().run()}><CaseUpper size={14}/> MAIÚSCULAS</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleLowercase().run()}><CaseLower size={14}/> minúsculas</DropdownItem>
          </ToolbarDropdown>

          <ToolbarDropdown icon={<Pilcrow size={15} />} label={editor.isActive('heading') ? `H${editor.getAttributes('heading').level}` : "P"} title="Estilo">
             <DropdownItem onClick={() => editor.chain().focus().setParagraph().run()}>Texto Normal</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>Título 1</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>Título 2</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>Título 3</DropdownItem>
          </ToolbarDropdown>

          <ToolbarDropdown icon={<AlignLeft size={15} />} title="Alinhamento">
             <DropdownItem onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft size={14}/> Esquerda</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter size={14}/> Centro</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight size={14}/> Direita</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().setTextAlign("justify").run()}><AlignJustify size={14}/> Justificado</DropdownItem>
          </ToolbarDropdown>

          <ToolbarDropdown icon={<BookOpen size={15}/>} title="Estrutura Legislativa" label="Legis">
             <DropdownItem onClick={inserirArtigo} title="Inserir Artigo"><span style={{fontWeight:'bold', minWidth:20}}>Art.</span> Artigo</DropdownItem>
             <DropdownItem onClick={inserirInciso} title="Inserir Inciso"><span style={{fontWeight:'bold', minWidth:20}}>I</span> Inciso</DropdownItem>
             <DropdownItem onClick={inserirAlinea} title="Inserir Alínea"><span style={{fontWeight:'bold', minWidth:20}}>a</span> Alínea</DropdownItem>
          {/*   <div style={{height:1, background:'#eee', margin:'2px 0'}}/>
            <div className={styles.dropdownSectionLabel} style={{ color: '#999', padding: '4px 8px', background:'#fafafa', textTransform:'uppercase'}}>ASSINATURAS</div>
             <DropdownItem onClick={inserirBlocoAssinaturaManual} title="Inserir Bloco Manual"><PenTool size={14} style={{marginRight:5}}/> Bloco Manual</DropdownItem>
             <DropdownItem onClick={() => inserirAssinaturaUsuario(currentUser)} title="Minha Assinatura"><UserCheck size={14} style={{marginRight:5}}/> Minha Assinatura</DropdownItem>
             {listaVereadores.length > 0 && (
               <div style={{borderTop: '1px dashed #eee', marginTop: 4, paddingTop: 4}}>
                 {listaVereadores.map(user => (
                   <DropdownItem key={user.id} onClick={() => inserirAssinaturaUsuario(user)} title={`Assinatura de ${user.nome}`}>
                      <Users size={12} style={{marginRight:5, opacity:0.7}}/> {user.alias || user.nome}
                   </DropdownItem>
                 ))}
               </div>
             )} */}
          </ToolbarDropdown>

          <ToolbarDropdown icon={<LayoutTemplate size={15} />} title="Modelos" label="Modelos">
             {modelosDisponiveis.length > 0 ? modelosDisponiveis.map(m => (
                <DropdownItem key={m.id} onClick={() => aplicarModelo(m.conteudoHtml)}>
                    <FileText size={14} style={{marginRight:5, opacity:0.6}}/> {m.nome || "Modelo sem título"}
                </DropdownItem>
             )) : <div style={{padding:'8px 12px', color:'#999'}}>Nenhum modelo.</div>}
          </ToolbarDropdown>

          <ToolbarDropdown icon={<TableIcon size={15} />} title="Tabelas">
             <DropdownItem onClick={insertTable}>Nova Tabela 3x3</DropdownItem>
          </ToolbarDropdown>

             {showDocxImport && <><ToolbarButton onClick={() => fileInputRef.current?.click()} title="Importar DOCX"><FileUp size={15} /> Importar</ToolbarButton><input className={styles.uploadInputHidden} type="file" ref={fileInputRef} onChange={(e)=>{if(e.target.files[0]) doImportDocx(e.target.files[0])}} accept=".docx" hidden /></>}
             <ToolbarButton onClick={exportToDocx} title="Baixar DOCX"><FileDown size={15}/></ToolbarButton>
             <ToolbarDropdown icon={<Printer size={15} />} title="Opções de Impressão">
                <DropdownItem onClick={() => handlePrint(false)}><FileText size={14} style={{marginRight:5}}/> Simples</DropdownItem>
                <DropdownItem onClick={() => handlePrint(true)}><Stamp size={14} style={{marginRight:5}}/> Com Cabeçalho</DropdownItem>
             </ToolbarDropdown>
             <div style={{width:1, background:'#ccc', margin:'0 4px'}} />
             <ToolbarButton onClick={handlePasteHtml} title="Colar HTML Renderizado"><ClipboardPaste size={15} /> Colar HTML</ToolbarButton>
             <ToolbarButton onClick={() => imgFileInputRef.current?.click()} title="Inserir Imagem"><ImageIcon size={15} /></ToolbarButton>
             <input className={styles.uploadInputHidden} ref={imgFileInputRef} type="file" accept="image/*" hidden onChange={(e) => {const f=e.target.files[0]; if(f) uploadImage(f).then(u=>u && editor.commands.setImage({src:u}));}} />
             <ToolbarButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Limpar"><Eraser size={15} /></ToolbarButton>
             <ToolbarButton onClick={() => setHtmlMode(true)} title="HTML"><Code2 size={15} /> Editar HTML</ToolbarButton>
             <ToolbarButton onClick={() => setFullscreen(!fullscreen)} title={fullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}>{fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}</ToolbarButton>

       </div>

       {/* ÁREA DO EDITOR */}
       <div ref={editorContainerRef} className="tiptap-host" style={{ height: fullscreen ? 'calc(100vh - 50px)' : '300px', overflowY: 'auto', backgroundColor: fullscreen ? '#e5e7eb' : '#fff', display: 'flex', justifyContent: 'center', padding: '20px' }}>
         
         {/* BUBBLE MENU IMAGEM */}
         {editor && (
           <BubbleMenu editor={editor} tippyOptions={{ duration: 100, maxWidth: 400 }} shouldShow={({ editor }) => editor.isActive('image')}>
             <div className="bubble-menu">
               <div className="bubble-row">
                   <div className="btn-group">
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { align: 'left' }).run()} className={editor.isActive('image', { align: 'left' }) ? 'is-active' : ''} title="Esquerda"><AlignLeft size={16}/></BubbleBtn>
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { align: 'center' }).run()} className={editor.isActive('image', { align: 'center' }) ? 'is-active' : ''} title="Centro"><AlignCenter size={16}/></BubbleBtn>
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { align: 'right' }).run()} className={editor.isActive('image', { align: 'right' }) ? 'is-active' : ''} title="Direita"><AlignRight size={16}/></BubbleBtn>
                   </div>
                   <div className="btn-group">
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { width: '25%' }).run()} style={{ fontWeight:600}}>25%</BubbleBtn>
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { width: '50%' }).run()} style={{ fontWeight:600}}>50%</BubbleBtn>
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { width: '100%' }).run()} style={{fontWeight:600}}>100%</BubbleBtn>
                   </div>
                   <BubbleBtn onClick={() => editor.chain().focus().deleteSelection().run()} className="danger" title="Remover"><Trash2 size={16}/></BubbleBtn>
               </div>
             </div>
           </BubbleMenu>
         )}

         {/* BUBBLE MENU TABELA */}
         {editor && (
           <BubbleMenu editor={editor} tippyOptions={{ duration: 100, maxWidth: 600 }} shouldShow={({ editor }) => editor.isActive('table')}>
              <div className="bubble-menu">
                 <div className="bubble-row" style={{justifyContent: 'space-between'}}>
                     <BubbleBtn onClick={toggleTableBorder} className={isTableBorderHidden ? 'is-active' : ''} style={{width:'auto', padding:'0 8px', gap:5}} title="Alternar Borda">
                         {isTableBorderHidden ? <><EyeOff size={14}/> Sem Borda (Oculta na Impressão)</> : <><Eye size={14}/> Com Borda (Sólida)</>}
                     </BubbleBtn>
                     <BubbleBtn onClick={() => editor.chain().focus().deleteTable().run()} className="danger" title="Excluir Tabela"><Trash2 size={14}/></BubbleBtn>
                 </div>
                 <div style={{height:1, background:'#eee'}} />

                 <div>
                   <div className="bubble-header">Colunas</div>
                   <div className="bubble-row">
                       <div className="btn-group">
                            <BubbleBtn onClick={() => editor.chain().focus().addColumnBefore().run()} title="Inserir à Esquerda"><ArrowLeft size={14}/></BubbleBtn>
                            <BubbleBtn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Inserir à Direita"><ArrowRight size={14}/></BubbleBtn>
                       </div>
                       <div className="btn-group">
                           <BubbleBtn onClick={() => editor.chain().focus().mergeCells().run()} title="Mesclar"><Grid size={14}/></BubbleBtn>
                           <BubbleBtn onClick={() => editor.chain().focus().splitCell().run()} title="Separar"><Grid size={14} style={{opacity:0.6}}/></BubbleBtn>
                       </div>
                       <span style={{flex:1}}/>
                       <BubbleBtn onClick={() => editor.chain().focus().deleteColumn().run()} className="danger" title="Excluir Coluna"><Minus size={14}/></BubbleBtn>
                   </div>
                 </div>

                 <div>
                   <div className="bubble-header">Linhas</div>
                   <div className="bubble-row">
                       <div className="btn-group">
                            <BubbleBtn onClick={() => editor.chain().focus().addRowBefore().run()} title="Inserir Acima"><ArrowUp size={14}/></BubbleBtn>
                            <BubbleBtn onClick={() => editor.chain().focus().addRowAfter().run()} title="Inserir Abaixo"><ArrowDown size={14}/></BubbleBtn>
                       </div>
                       <span style={{flex:1}}/>
                       <BubbleBtn onClick={() => editor.chain().focus().deleteRow().run()} className="danger" title="Excluir Linha"><Minus size={14}/></BubbleBtn>
                   </div>
                 </div>
              </div>
           </BubbleMenu>
         )}

         <EditorContent editor={editor} className="tiptap" style={{ height: fullscreen ? "auto" : "auto", maxHeight: fullscreen ? "800px" : "300px", width: fullscreen ? "210mm" : "100%", maxWidth: "100%", backgroundColor: "#fff", padding: fullscreen ? "9mm" : "10px", boxShadow: fullscreen ? "0 0 10px rgba(0,0,0,0.1)" : "none", outline: "none", overflow: "auto" }} />
         
         {blockMenu.visible && (
           <div className="block-kebab" style={{ left: blockMenu.x, top: blockMenu.y }} onMouseDown={(e) => e.preventDefault()}>
               <button type="button" className="kebab-btn"><MoreVertical size={14} /></button>
           </div>
         )}
       </div>

       {htmlMode && ReactDOM.createPortal(
         <div className={styles.modalOverlay} style={{zIndex: 10000}}>
             <div className={styles.modal} style={{width:'800px', background:'white', padding:20, borderRadius:5}}>
                 <div className={styles.modalTitle}><h2>Editar Código HTML</h2></div>
                 <textarea value={htmlBuffer || editor.getHTML()} onChange={e => setHtmlBuffer(e.target.value)} className={styles.textArea} style={{width:'100%', height:'400px', fontFamily:'monospace'}} />
                 <div className={styles.botoesAcaoBlocos}>
                     <button className={styles.encerrarVotacao} onClick={() => setHtmlMode(false)}>Cancelar</button>
                     <button className={styles.iniciarVotacao} onClick={() => { editor.commands.setContent(htmlBuffer); setHtmlMode(false); }}>Aplicar</button>
                 </div>
             </div>
         </div>, document.body
       )}
    </div>
  );
}